import type { z } from "zod";
import { NoAuthError } from "@utils/NoAuthError";
import { RouterOutputs, trpc } from "@utils/trpc";
import { getQueryKey } from "@trpc/react-query";
import { favouritesListSchema } from "@libs/wire";
import type { DataSession } from "../../../data/core/session";

export type FavouritesQueryData = RouterOutputs["user"]["listFavourites"];

/**
 * Compile-time drift check, bidirectional. See useLibrary/queryFunctions.ts
 * for the full rationale: catches both "schema is missing fields the
 * router has" and "schema is narrower than the router output."
 */
type _FavouritesWireSchemaSubset =
  z.infer<typeof favouritesListSchema> extends FavouritesQueryData
    ? true
    : never;
type _FavouritesWireSchemaSuperset =
  FavouritesQueryData extends z.infer<typeof favouritesListSchema>
    ? true
    : never;
const _favouritesWireSchemaDriftCheck: [
  _FavouritesWireSchemaSubset,
  _FavouritesWireSchemaSuperset,
] = [true, true];
void _favouritesWireSchemaDriftCheck;

export const favouritesQueryKey = () => getQueryKey(trpc.user.listFavourites);

export const favouritesQuery = (data: DataSession) =>
  data.cache.durableQueryOptions({
    queryKey: favouritesQueryKey(),
    queryFn: () => {
      if (!data.auth.currentUser()) {
        throw new NoAuthError("favouritesQuery queryFn");
      }
      return data.api.user.listFavourites.query();
    },
    schema: favouritesListSchema,
    // 5 min (2026-08-03, was 1h): favourites are a cheap live RFM query over
    // the user's own plays — an hour of staleness meant a NEW user's first
    // favourites stayed invisible for the whole session.
    staleTime: 1000 * 60 * 5,
  });
